/**
 * Contextual, deferrable post-trip review invitation.
 *
 * The product asks one question about the complete trip. The server derives
 * every reputation projection from that single immutable event; this surface
 * never asks the person to choose a subject or repeat the rating.
 *
 * The invitation deliberately fails open. It only appears in Trips, never
 * intercepts a booking or browsing task, and can be deferred for a day. A
 * query error shows nothing, a rejected trip is hidden for the session, and
 * two consecutive transport failures also release the person until the next
 * app launch.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { ScrollView, Text, View } from 'react-native'
import { usePathname } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { AppModal } from '@/components/ui/AppModal'
import { GlassSheet } from '@/components/ui/GlassSheet'
import { Avatar } from '@/components/ui/Avatar'
import { Button } from '@/components/ui/Button'
import { Image } from '@/components/ui/Image'
import { Pressable } from '@/components/ui/Pressable'
import { Rating } from '@/components/ui/Rating'
import { TextInput } from '@/components/ui/TextInput'
import { MessageCircle } from '@/components/ui/icons'
import { FONTS, RADIUS, SPACING } from '@/lib/brand'
import { ServiceError } from '@/lib/errors/service-error'
import { usePendingTripReview, useSubmitTripReview } from '@/lib/hooks/use-reviews'
import {
  createReviewNudgeDeferral,
  isReviewNudgeDeferred,
  parseReviewNudgeDeferral,
  type ReviewNudgeDeferral,
} from '@/lib/reviews/review-nudge'
import { SecureStorageAdapter } from '@/lib/storage/secure-storage'
import { useTheme } from '@/lib/hooks/use-theme'
import { useSemanticColors } from '@/lib/theme/tokens'
import { toast } from '@/lib/toast'
import { isOtaCriticalRoute } from '@/lib/updates/ota-policy'
import { notification, NotificationType } from '@/lib/utils/haptics'
import { captureError, errorMessage } from '@/lib/utils/observability'

const MAX_COMMENT = 1000
const REVIEW_NUDGE_DEFER_KEY = 'tago.review_nudge.defer.v1'
const TERMINAL_REVIEW_ERROR_CODES = new Set([
  'VALIDATION_ERROR',
  '23503',
  '23505',
  '23514',
  '42501',
])

function appPath(pathname: string | null | undefined): string {
  return (pathname ?? '').replace(/^\/\(tabs\)/, '') || '/'
}

function hasLocalReviewSurface(pathname: string | null | undefined): boolean {
  const path = appPath(pathname)
  return (
    path === '/booking/review' ||
    path.startsWith('/booking/review/') ||
    /^\/trips\/[^/]+\/?$/.test(path)
  )
}

/** A review is contextual on Trips; it must not become a lid over exploration. */
function isReviewPromptSurface(pathname: string | null | undefined): boolean {
  return appPath(pathname) === '/trips'
}

export function MandatoryReviewGate() {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const insets = useSafeAreaInsets()
  const pathname = usePathname()
  const reviewSurfaceActive =
    isReviewPromptSurface(pathname) &&
    !isOtaCriticalRoute(pathname) &&
    !hasLocalReviewSurface(pathname)

  const { data: pending } = usePendingTripReview({ enabled: reviewSurfaceActive })
  const { submitTripReview, isPending } = useSubmitTripReview()
  const pendingRentalId = pending?.rentalId

  const [rating, setRating] = useState(0)
  const [comment, setComment] = useState('')
  const [showComment, setShowComment] = useState(false)
  const [hiddenRentalIds, setHiddenRentalIds] = useState<readonly string[]>([])
  const [consecutiveFailures, setConsecutiveFailures] = useState(0)
  const [deferralReady, setDeferralReady] = useState(false)
  const [deferral, setDeferral] = useState<ReviewNudgeDeferral | null>(null)

  useEffect(() => {
    let cancelled = false

    void SecureStorageAdapter.getItem(REVIEW_NUDGE_DEFER_KEY)
      .then((raw) => {
        const parsed = parseReviewNudgeDeferral(raw)
        if (raw && !parsed) void SecureStorageAdapter.removeItem(REVIEW_NUDGE_DEFER_KEY)
        if (cancelled) return
        setDeferral(parsed)
        setDeferralReady(true)
      })
      .catch(() => {
        if (cancelled) return
        setDeferralReady(true)
      })

    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    setRating(0)
    setComment('')
    setShowComment(false)
    setConsecutiveFailures(0)
  }, [pendingRentalId])

  // The server returns the oldest pending trip. Once that changes, a stored
  // deferral for the earlier trip must not hide the next one.
  useEffect(() => {
    if (!pendingRentalId || !deferral || deferral.rentalId === pendingRentalId) return
    setDeferral(null)
    void SecureStorageAdapter.removeItem(REVIEW_NUDGE_DEFER_KEY)
  }, [deferral, pendingRentalId])

  const visible =
    reviewSurfaceActive &&
    deferralReady &&
    !!pending &&
    !hiddenRentalIds.includes(pending.rentalId) &&
    !isReviewNudgeDeferred(pending.rentalId, deferral)

  const counterpartyName = pending?.counterpartyName.trim() ?? ''
  const counterpartyLabel = counterpartyName || t('reviews.reviewer_fallback')
  const isGuest = pending?.reviewerRole === 'guest'
  const title = isGuest
    ? t('review_gate.title_guest')
    : counterpartyName
      ? t('review_gate.title_host', { name: counterpartyName })
      : t('review_gate.title_fallback')
  const context = pending
    ? counterpartyName
      ? t(isGuest ? 'review_gate.context_guest' : 'review_gate.context_host', {
          name: counterpartyName,
          vehicle: pending.vehicleTitle,
        })
      : t('review_gate.context_fallback', { vehicle: pending.vehicleTitle })
    : ''

  const ratingLabel = useMemo(
    () => (rating >= 1 && rating <= 5 ? t(`review_gate.rating_${rating}`) : ''),
    [rating, t],
  )

  const hideForSession = useCallback((rentalId: string) => {
    setHiddenRentalIds((current) => (current.includes(rentalId) ? current : [...current, rentalId]))
  }, [])

  const defer = useCallback(() => {
    if (!pending) return
    const next = createReviewNudgeDeferral(pending.rentalId)
    setDeferral(next)
    hideForSession(pending.rentalId)
    void SecureStorageAdapter.setItem(REVIEW_NUDGE_DEFER_KEY, JSON.stringify(next))
  }, [hideForSession, pending])

  const submit = useCallback(async () => {
    if (!pending || rating < 1 || isPending) return

    const cleanComment = comment.trim()

    try {
      await submitTripReview({
        rentalId: pending.rentalId,
        rating,
        comment: cleanComment || undefined,
      })

      void notification(NotificationType.Success)
      setConsecutiveFailures(0)
      // Hide immediately; query invalidation can then reveal the next trip
      // without flashing this completed sheet again.
      hideForSession(pending.rentalId)
    } catch (error) {
      void notification(NotificationType.Error)
      captureError(error, { screen: 'review-gate', action: 'submit-trip-review' })

      if (error instanceof ServiceError && TERMINAL_REVIEW_ERROR_CODES.has(error.code)) {
        hideForSession(pending.rentalId)
        return
      }

      const failures = consecutiveFailures + 1
      setConsecutiveFailures(failures)
      if (failures >= 2) {
        hideForSession(pending.rentalId)
        toast.error(t('review_gate.error_title'), {
          description: t('review_gate.error_later'),
        })
        return
      }

      toast.error(t('review_gate.error_title'), { description: errorMessage(error, '') })
    }
  }, [
    comment,
    consecutiveFailures,
    hideForSession,
    isPending,
    pending,
    rating,
    submitTripReview,
    t,
  ])

  if (!visible || !pending) return null

  return (
    <AppModal visible transparent animationType="slide" statusBarTranslucent onRequestClose={defer}>
      <View
        style={{
          flex: 1,
          justifyContent: 'flex-end',
        }}
        testID="review-gate"
      >
        <GlassSheet
          accessibilityViewIsModal
          accessibilityLabel={title}
          style={{
            width: '100%',
            maxWidth: 560,
            maxHeight: '92%',
            alignSelf: 'center',
            borderTopLeftRadius: RADIUS.xl,
            borderTopRightRadius: RADIUS.xl,
          }}
          contentStyle={{
            paddingTop: SPACING.xl,
            paddingHorizontal: SPACING.lg,
            paddingBottom: insets.bottom + SPACING.lg,
          }}
        >
          <ScrollView
            style={{ flexShrink: 1 }}
            contentContainerStyle={{ gap: SPACING.xl, paddingBottom: SPACING.xs }}
            keyboardShouldPersistTaps="handled"
            showsVerticalScrollIndicator={false}
          >
            <View style={{ alignItems: 'center', gap: SPACING.sm }}>
              <View style={{ paddingRight: SPACING.md, paddingBottom: SPACING.sm }}>
                <Image
                  source={pending.vehicleImage}
                  width={104}
                  height={76}
                  borderRadius={RADIUS.lg}
                  contentFit="cover"
                  accessibilityLabel={pending.vehicleTitle}
                />
                <View
                  style={{
                    position: 'absolute',
                    right: 0,
                    bottom: 0,
                    padding: 2,
                    borderRadius: RADIUS.full,
                    backgroundColor: colors.background,
                  }}
                >
                  <Avatar
                    source={pending.counterpartyAvatar}
                    fallback={counterpartyLabel}
                    colorSeed={pending.counterpartyId}
                    size="sm"
                    accessibilityLabel={counterpartyLabel}
                  />
                </View>
              </View>

              <Text
                style={{
                  fontFamily: FONTS.bodyBold,
                  fontSize: 12,
                  letterSpacing: 0.8,
                  color: colors.textMuted,
                  textTransform: 'uppercase',
                }}
              >
                {t('review_gate.kicker')}
              </Text>
              <Text
                accessibilityRole="header"
                style={{
                  maxWidth: 420,
                  fontFamily: FONTS.bodyBold,
                  fontSize: 24,
                  lineHeight: 30,
                  color: colors.textPrimary,
                  textAlign: 'center',
                }}
              >
                {title}
              </Text>
              <Text
                numberOfLines={2}
                style={{
                  maxWidth: 420,
                  fontFamily: FONTS.body,
                  fontSize: 15,
                  lineHeight: 21,
                  color: colors.textSecondary,
                  textAlign: 'center',
                }}
              >
                {context}
              </Text>
            </View>

            <View style={{ alignItems: 'center', gap: SPACING.sm }}>
              <Text
                style={{
                  fontFamily: FONTS.body,
                  fontSize: 14,
                  color: colors.textSecondary,
                }}
              >
                {t('review_gate.stars_label')}
              </Text>
              <Rating value={rating} onChange={setRating} size={40} testID="review-gate.rating" />
              <Text
                style={{
                  minHeight: 21,
                  fontFamily: FONTS.bodyBold,
                  fontSize: 15,
                  lineHeight: 21,
                  color: rating > 0 ? colors.textPrimary : colors.textMuted,
                }}
              >
                {ratingLabel || ' '}
              </Text>
            </View>

            {showComment ? (
              <View style={{ gap: SPACING.xs }}>
                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel={t('reviews.hide_comment')}
                  accessibilityState={{ expanded: true }}
                  onPress={() => setShowComment(false)}
                  style={({ pressed }) => ({
                    minHeight: 44,
                    alignSelf: 'flex-start',
                    justifyContent: 'center',
                    opacity: pressed ? 0.65 : 1,
                  })}
                >
                  <Text
                    style={{
                      fontFamily: FONTS.bodyBold,
                      fontSize: 14,
                      color: colors.textSecondary,
                    }}
                  >
                    {t('reviews.hide_comment')}
                  </Text>
                </Pressable>
                <TextInput
                  label={t('review_gate.comment_label')}
                  placeholder={t('review_gate.comment_placeholder')}
                  value={comment}
                  onChangeText={(value) => setComment(value.slice(0, MAX_COMMENT))}
                  multiline
                  numberOfLines={3}
                  autoFocus
                  testID="review-gate.comment"
                  style={{ minHeight: 88, textAlignVertical: 'top' }}
                />
                <Text
                  style={{
                    alignSelf: 'flex-end',
                    fontFamily: FONTS.body,
                    fontSize: 12,
                    color: colors.textMuted,
                  }}
                >
                  {t('reviews.char_count', { count: comment.length, max: MAX_COMMENT })}
                </Text>
              </View>
            ) : (
              <Pressable
                accessibilityRole="button"
                accessibilityLabel={t('review_gate.add_comment')}
                accessibilityState={{ expanded: false }}
                onPress={() => setShowComment(true)}
                testID="review-gate.add-comment"
                style={({ pressed }) => ({
                  minHeight: 48,
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: SPACING.sm,
                  borderRadius: RADIUS.md,
                  backgroundColor: colors.surface,
                  opacity: pressed ? 0.65 : 1,
                })}
              >
                <MessageCircle size={18} color={colors.textSecondary} strokeWidth={1.8} />
                <Text
                  style={{
                    fontFamily: FONTS.bodyBold,
                    fontSize: 14,
                    color: colors.textSecondary,
                  }}
                >
                  {t('review_gate.add_comment')}
                </Text>
              </Pressable>
            )}
          </ScrollView>

          <View style={{ paddingTop: SPACING.lg, gap: SPACING.sm }}>
            <Button
              title={t('review_gate.defer')}
              variant="text"
              onPress={defer}
              testID="review-gate.defer"
            />
            <Button
              title={t('review_gate.submit')}
              onPress={() => void submit()}
              loading={isPending}
              disabled={rating < 1 || isPending}
              testID="review-gate.submit"
            />
          </View>
        </GlassSheet>
      </View>
    </AppModal>
  )
}
